import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { trackEvent } from '@/helpers/external-tools-helper';
import { DEMO_MODE_EVENTS } from '@/analytics/demo-mode';
import { useChangeBusiness } from '@/hooks/api/business';
import { useSetPermissions } from '@/hooks/api/user';
import { toast } from '@/hooks/use-toast';
import { UserContainer, ClientContainer } from '@/providers';
import { ConnectionQuery } from '@/queries/connection';
import { useDemoMode } from '@/hooks/use-demo-mode';
import { demoModeStorage } from '@/utils/demo-mode-storage';
import { SpotlightGuide } from '@/components/spotlight-guide';
import { cn } from '@/lib/utils';

const FUN_MESSAGES = [
  'Loading demo workspace...',
  'Preparing sample data...',
  'Almost there...',
  'Setting up your tour...',
  'Making things sparkle...',
];

const FullScreenLoader = () => {
  const [message, setMessage] = useState(FUN_MESSAGES[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessage(FUN_MESSAGES[Math.floor(Math.random() * FUN_MESSAGES.length)]);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-4 p-8 rounded-lg bg-card shadow-lg border">
        <div className="relative">
          <Spinner size="lg" className="text-primary" />
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold tracking-tight">{message}</h2>
          <p className="text-sm text-muted-foreground">
            The app will reload in a moment...
          </p>
        </div>
      </div>
    </div>
  );
};

export const DemoModeNudgeNotification: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSpotlight, setShowSpotlight] = useState(false);

  const navigate = useNavigate();
  const { state: userState } = UserContainer.useContainer();
  const { state: clientState } = ClientContainer.useContainer();
  const { isDemoMode } = useDemoMode();

  const changeBusiness = useChangeBusiness();
  const setCurrentBusinessPermission = useSetPermissions();

  // Fetch datasources for current business (use optional chaining for conditional fetching)
  const {
    data: providerAccounts,
    isLoading: isDataSourcesLoading,
  } = ConnectionQuery.useDataSources(userState.business?.id || '');

  // Show notification after 20 seconds
  useEffect(() => {
    // Wait for data to load
    if (isDataSourcesLoading || !userState.business) {
      return undefined;
    }

    const hasDataSources = providerAccounts && providerAccounts.length > 0;

    // Check if notification should be shown using centralized utility
    if (demoModeStorage.shouldShowNudge(hasDataSources, isDemoMode)) {
      // 20 second delay to let user explore first
      const timer = setTimeout(() => {
        setIsVisible(true);
        trackEvent(DEMO_MODE_EVENTS.DEMO_NUDGE_SHOWN, {
          businessId: userState.business?.id,
          clientId: clientState.selectedClient?.id,
        });
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [
    providerAccounts,
    isDemoMode,
    userState.business,
    clientState.selectedClient?.id,
    isDataSourcesLoading,
  ]);

  // Auto-expand briefly when first shown, then collapse
  useEffect(() => {
    if (isVisible) {
      // Show expanded for 3 seconds, then collapse
      setIsExpanded(true);
      const expandTimer = setTimeout(() => {
        setIsExpanded(false);
      }, 3000);

      return () => clearTimeout(expandTimer);
    }

    return undefined;
  }, [isVisible]);

  const handleNotificationClose = useCallback(
    (dismissType = 'manual_dismiss') => {
      demoModeStorage.markNudgeAsSeen();
      setIsVisible(false);
      trackEvent(DEMO_MODE_EVENTS.DEMO_NUDGE_SKIPPED, {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
        dismissType,
      });

      // Show spotlight guide if user hasn't seen it yet
      if (demoModeStorage.shouldShowSpotlight()) {
        setShowSpotlight(true);
      }
    },
    [userState.business?.id, clientState.selectedClient?.id]
  );

  const handleSkip = useCallback(() => {
    handleNotificationClose('manual_dismiss');
  }, [handleNotificationClose]);

  const handleToggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
    trackEvent(DEMO_MODE_EVENTS.DEMO_NUDGE_SHOWN, {
      businessId: userState.business?.id,
      clientId: clientState.selectedClient?.id,
      action: isExpanded ? 'collapsed' : 'expanded',
    });
  }, [isExpanded, userState.business?.id, clientState.selectedClient?.id]);

  const handleExploreDemo = useCallback(async () => {
    try {
      setIsLoading(true);

      // Find demo business from businessOptions
      const demoBusiness = userState.businessOptions?.find(
        (business: any) => business.is_demo_account === true
      );

      if (!demoBusiness) {
        setIsLoading(false);
        throw new Error('Demo business not found');
      }

      // Set localStorage flags using centralized utility
      demoModeStorage.markNudgeAccepted();

      // Track acceptance and demo mode entry
      trackEvent(DEMO_MODE_EVENTS.DEMO_NUDGE_ACCEPTED, {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
        demoBusinessId: demoBusiness.id,
      });

      trackEvent(DEMO_MODE_EVENTS.DEMO_MODE_ENTERED, {
        businessId: userState.business?.id,
        demoBusinessId: demoBusiness.id,
      });

      // Close notification
      setIsVisible(false);

      // Switch business - following exact pattern from account view
      setCurrentBusinessPermission(demoBusiness.id);
      await changeBusiness(demoBusiness);

      // Navigate to social media analytics page for demo tour
      const demoClientId = demoBusiness?.clients[0]?.id;
      const socialMediaUrl = `/ws/${demoClientId}/analytics/social`;

      // Navigate to new URL and reload
      navigate(socialMediaUrl, { replace: true });

      // Reload after delay to show loading messages
      setTimeout(() => {
        window.location.reload();
      }, 3000); // Give users more time to read the fun messages
    } catch (error) {
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: 'Unable to load demo mode. Please try again.',
      });
    }
  }, [
    userState.business?.id,
    userState.businessOptions,
    clientState?.selectedClient?.id,
    setCurrentBusinessPermission,
    changeBusiness,
    navigate,
  ]);

  const handleSpotlightDismiss = useCallback(() => {
    demoModeStorage.markSpotlightAsSeen();
    setShowSpotlight(false);
    trackEvent(DEMO_MODE_EVENTS.DEMO_NUDGE_SKIPPED, {
      businessId: userState.business?.id,
      clientId: clientState.selectedClient?.id,
      spotlightDismissed: true,
    });
  }, [userState.business?.id, clientState.selectedClient?.id]);

  // Don't render anything if there's no business (user not authenticated)
  if (!userState.business?.id) {
    return null;
  }

  if (!isVisible) {
    return (
      <>
        {isLoading && <FullScreenLoader />}
        {showSpotlight && (
          <SpotlightGuide
            targetSelector="#try-demo-mode-button"
            onDismiss={handleSpotlightDismiss}
            message="Changed your mind? Try demo mode here!"
          />
        )}
      </>
    );
  }

  return (
    <>
      {isLoading && <FullScreenLoader />}
      {showSpotlight && (
        <SpotlightGuide
          targetSelector="#try-demo-mode-button"
          onDismiss={handleSpotlightDismiss}
          message="Changed your mind? Try demo mode here!"
        />
      )}

      {/* Collapsible notification - LinkedIn/Gmail style */}
      <div
        className={cn(
          'fixed bottom-4 right-4 z-50',
          'transition-all duration-300 ease-in-out',
          isExpanded ? 'w-80' : 'w-64'
        )}
      >
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
          {/* Collapsed header bar (always visible) */}
          <button
            onClick={handleToggleExpand}
            className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0">
                <i className="ti ti-sparkles text-lg text-primary" />
              </div>
              <div className="flex-1 min-w-0 text-left">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  Try Demo Mode
                </h3>
                <p className="text-xs text-gray-600 truncate">
                  Explore with sample data
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleSkip();
                }}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close notification"
              >
                <i className="ti ti-x text-base" />
              </button>
              <i
                className={cn(
                  'ti text-base text-gray-400 transition-transform duration-200',
                  isExpanded ? 'ti-chevron-down' : 'ti-chevron-up'
                )}
              />
            </div>
          </button>

          {/* Expanded content */}
          <div
            className={cn(
              'transition-all duration-300 ease-in-out overflow-hidden',
              isExpanded
                ? 'max-h-96 opacity-100'
                : 'max-h-0 opacity-0 pointer-events-none'
            )}
          >
            <div className="px-4 pb-4 space-y-3 border-t border-gray-100">
              {/* Description */}
              <p className="text-xs text-gray-600 leading-relaxed pt-3">
                Take a guided tour with realistic sample data. Perfect for
                exploring features before connecting your accounts.
              </p>

              {/* Action buttons */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="flex-1 text-xs h-8 px-3 py-1"
                  disabled={isLoading}
                >
                  Maybe later
                </Button>
                <Button
                  onClick={handleExploreDemo}
                  className="flex-1 gap-1.5 text-xs h-8 px-3 py-1"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <>
                      <i className="ti ti-arrow-right text-sm" />
                      <span>Start Tour</span>
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
